import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowRight, Lock, TrendingUp, DollarSign, BarChart as BarChartIcon, Target } from 'lucide-react';
import { useUser } from '../context/UserContext';
import Analytics from '../services/analytics';

interface InvestmentGoalCalculatorProps {
    openPopup: (signUpMode?: boolean) => void;
}

export function InvestmentGoalCalculator({ openPopup }: InvestmentGoalCalculatorProps) {
    const { user } = useUser();
    const [goalAmount, setGoalAmount] = useState<number>(1000000);
    const [initialInvestment, setInitialInvestment] = useState<number>(10000); // New State
    const [monthlyContribution, setMonthlyContribution] = useState<number>(1000); // Default to 1k
    const [annualReturn, setAnnualReturn] = useState<{ type: 'market' | 'custom', value: number }>({ type: 'market', value: 7.2 });
    const [dividendYield, setDividendYield] = useState<{ type: 'market' | 'custom', value: number }>({ type: 'market', value: 1.0 });
    const [dividendGrowth, setDividendGrowth] = useState<{ type: 'market' | 'custom', value: number }>({ type: 'market', value: 5.0 });
    const [showResult, setShowResult] = useState<boolean>(false);

    // Helper inputs state (for toggles)
    const [contributionMode, setContributionMode] = useState<'avg' | 'custom'>('avg');

    // Projection State
    const [yearsToGoal, setYearsToGoal] = useState<number>(0);
    const [chartData, setChartData] = useState<any[]>([]);

    // Calculation Logic
    useEffect(() => {
        let balance = initialInvestment;
        let totalContributed = initialInvestment;
        let year = 0;
        const maxYears = 50;
        const data = [];

        // Simple Model:
        // We assume re-investment of dividends.
        // Total Return = Price Appreciation + Dividend Yield.
        // To respect the "Dividend Growth" input from the user (which usually implies yield on cost increases),
        // we can model it as: Price grows by 'annualReturn', Dividend Payout grows by 'dividendGrowth'.
        // If you reinvest, you buy more shares, further accelerating expectation.
        // To keep it robust but simple:
        // Annual Gain = (Balance * PriceReturn) + (Balance * DivYield).
        // And DivYield itself grows? No, usually yield stays constant if price grows.
        // If user says "Dividend Growth 5%", they mean the $ payout grows.
        // Let's assume the user input "Return" is purely Price Appreciation (as per screenshot "Portfolio Annual Returns").

        const monthlyContrib = monthlyContribution;
        const priceReturnRate = annualReturn.value / 100;
        // Base dividend yield
        let currentDivYield = dividendYield.value / 100;
        const divGrowthRate = dividendGrowth.value / 100;

        data.push({
            year: 'Today',
            amount: balance,
            contributions: totalContributed,
            return: 0
        });

        while (balance < goalAmount && year < maxYears) {
            year++;

            const annualContrib = monthlyContrib * 12;

            // 1. Price Appreciation on existing assets
            // (Using simple start-of-year balance for growth)
            const priceGrowth = balance * priceReturnRate;

            // 2. Dividend Income
            // Income = Balance * Current Yield
            const dividendIncome = balance * currentDivYield;

            // 3. New End Balance = Old + Contrib + PriceGrowth + Reinvested Dividends
            // (Simplified: contributions don't earn full return in year 1, but let's be generous/simple)
            balance = balance + annualContrib + priceGrowth + dividendIncome;
            totalContributed += annualContrib;

            // Chart Logic: Log every year
            const totalReturn = Math.max(0, balance - totalContributed);
            data.push({
                year: `${year}y`,
                // We split amount into two for stacked chart
                amount: Math.round(balance),
                contributions: Math.round(totalContributed),
                return: Math.round(totalReturn)
            });
        }

        // Fill gaps if logic finished too fast
        if (data.length < 5) {
            // Force some intermediate points if needed, but the loop handles it logic wise.
        }

        setYearsToGoal(year);
        setChartData(data);

    }, [goalAmount, initialInvestment, monthlyContribution, annualReturn, dividendYield, dividendGrowth]);

    const handleReveal = () => {
        // Analytics: Track Intent
        Analytics.event('Lead Magnet Engage', {
            event_category: 'Calculator',
            event_label: 'Investment Goal Attempt',
            value: goalAmount
        });

        if (user) {
            setShowResult(true);
        } else {
            openPopup(true);
        }
    };

    // Auto-unlock if user logs in
    useEffect(() => {
        if (user) {
            setShowResult(true);
        }
    }, [user]);

    return (
        <div className="min-h-screen bg-theme-primary text-theme-primary flex flex-col items-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl w-full space-y-8">
                <div className="text-center">
                    <h1 className="text-3xl font-extrabold text-theme-primary sm:text-4xl">
                        Investing Potential Calculator
                    </h1>
                    <p className="mt-4 text-xl text-theme-secondary">
                        How long until you hit your number?
                    </p>
                </div>

                <div className="bg-theme-secondary rounded-2xl shadow-xl overflow-hidden border border-theme-tertiary">
                    <div className="p-6 sm:p-8 space-y-8">

                        {/* Input: Goal */}
                        <div className="text-center">
                            <label className="block text-sm font-medium text-theme-muted mb-2">Portfolio Goal</label>
                            <div className="flex justify-center items-center space-x-4">
                                <button
                                    onClick={() => setGoalAmount(Math.max(0, goalAmount - 100000))}
                                    className="p-2 rounded-full bg-theme-tertiary hover:bg-theme-primary text-blue-400"
                                >
                                    -
                                </button>
                                <div className="text-4xl font-bold text-theme-primary tracking-tight">
                                    ${goalAmount.toLocaleString()}
                                </div>
                                <button
                                    onClick={() => setGoalAmount(goalAmount + 100000)}
                                    className="p-2 rounded-full bg-theme-tertiary hover:bg-theme-primary text-blue-400"
                                >
                                    +
                                </button>
                            </div>
                        </div>

                        {/* Inputs Section */}
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            {/* NEW: Initial Investment */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-theme-secondary flex items-center gap-2">
                                    <DollarSign className="w-4 h-4 text-emerald-400" /> Initial Investment
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2 text-theme-muted">$</span>
                                    <input
                                        type="number"
                                        value={String(initialInvestment)}
                                        onChange={(e) => setInitialInvestment(Number(e.target.value))}
                                        className="w-full bg-theme-primary border border-theme-tertiary rounded-md py-2 pl-6 pr-3 text-theme-primary focus:ring-2 focus:ring-blue-500 outline-none no-spinner"
                                    />
                                </div>
                            </div>

                            {/* Contributions */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-theme-secondary flex items-center gap-2">
                                    <DollarSign className="w-4 h-4 text-emerald-400" /> Monthly Deposits
                                </label>
                                <div className="bg-theme-primary rounded-lg p-1 flex border border-theme-tertiary">
                                    <button
                                        onClick={() => { setContributionMode('avg'); setMonthlyContribution(1000); }}
                                        className={`flex - 1 py - 2 text - sm rounded - md transition - all ${contributionMode === 'avg' ? 'bg-theme-secondary text-theme-primary shadow' : 'text-theme-muted hover:text-theme-primary'} `}
                                    >
                                        Avg ($1k)
                                    </button>
                                    <button
                                        onClick={() => setContributionMode('custom')}
                                        className={`flex - 1 py - 2 text - sm rounded - md transition - all ${contributionMode === 'custom' ? 'bg-theme-secondary text-theme-primary shadow' : 'text-theme-muted hover:text-theme-primary'} `}
                                    >
                                        Custom
                                    </button>
                                </div>
                                {contributionMode === 'custom' && (
                                    <div className="relative">
                                        <span className="absolute left-3 top-2 text-theme-muted">$</span>
                                        <input
                                            type="number"
                                            value={String(monthlyContribution)}
                                            onChange={(e) => setMonthlyContribution(Number(e.target.value))}
                                            className="w-full bg-theme-primary border border-theme-tertiary rounded-md py-2 pl-6 pr-3 text-theme-primary focus:ring-2 focus:ring-blue-500 outline-none no-spinner"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Annual Returns */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-theme-secondary flex items-center gap-2">
                                    <TrendingUp className="w-4 h-4 text-blue-400" /> Annual Return
                                </label>
                                <div className="bg-theme-primary rounded-lg p-1 flex border border-theme-tertiary">
                                    <button
                                        onClick={() => setAnnualReturn({ type: 'market', value: 7.2 })}
                                        className={`flex - 1 py - 2 text - sm rounded - md transition - all ${annualReturn.type === 'market' ? 'bg-theme-secondary text-theme-primary shadow' : 'text-theme-muted hover:text-theme-primary'} `}
                                    >
                                        S&P 500 (7.2%)
                                    </button>
                                    <button
                                        onClick={() => setAnnualReturn({ ...annualReturn, type: 'custom' })}
                                        className={`flex - 1 py - 2 text - sm rounded - md transition - all ${annualReturn.type === 'custom' ? 'bg-theme-secondary text-theme-primary shadow' : 'text-theme-muted hover:text-theme-primary'} `}
                                    >
                                        Custom
                                    </button>
                                </div>
                                {annualReturn.type === 'custom' && (
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={String(annualReturn.value)}
                                            onChange={(e) => setAnnualReturn({ type: 'custom', value: Number(e.target.value) })}
                                            className="w-full bg-theme-primary border border-theme-tertiary rounded-md py-2 px-3 text-theme-primary focus:ring-2 focus:ring-blue-500 outline-none no-spinner"
                                        />
                                        <span className="absolute right-3 top-2 text-theme-muted">%</span>
                                    </div>
                                )}
                            </div>

                            {/* Dividend Yield */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-theme-secondary flex items-center gap-2">
                                    <BarChartIcon className="w-4 h-4 text-purple-400" /> Dividend Yield
                                </label>
                                <div className="bg-theme-primary rounded-lg p-1 flex border border-theme-tertiary">
                                    <button
                                        onClick={() => setDividendYield({ type: 'market', value: 1.0 })}
                                        className={`flex - 1 py - 2 text - sm rounded - md transition - all ${dividendYield.type === 'market' ? 'bg-theme-secondary text-theme-primary shadow' : 'text-theme-muted hover:text-theme-primary'} `}
                                    >
                                        Avg (1.0%)
                                    </button>
                                    <button
                                        onClick={() => setDividendYield({ ...dividendYield, type: 'custom' })}
                                        className={`flex - 1 py - 2 text - sm rounded - md transition - all ${dividendYield.type === 'custom' ? 'bg-theme-secondary text-theme-primary shadow' : 'text-theme-muted hover:text-theme-primary'} `}
                                    >
                                        Custom
                                    </button>
                                </div>
                                {dividendYield.type === 'custom' && (
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={String(dividendYield.value)}
                                            onChange={(e) => setDividendYield({ type: 'custom', value: Number(e.target.value) })}
                                            className="w-full bg-theme-primary border border-theme-tertiary rounded-md py-2 px-3 text-theme-primary focus:ring-2 focus:ring-blue-500 outline-none no-spinner"
                                        />
                                        <span className="absolute right-3 top-2 text-theme-muted">%</span>
                                    </div>
                                )}
                            </div>

                            {/* Dividend Growth */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-theme-secondary flex items-center gap-2">
                                    <Target className="w-4 h-4 text-green-400" /> Div. Growth Rate
                                </label>
                                <div className="bg-theme-primary rounded-lg p-1 flex border border-theme-tertiary">
                                    <button
                                        onClick={() => setDividendGrowth({ type: 'market', value: 5.0 })}
                                        className={`flex - 1 py - 2 text - sm rounded - md transition - all ${dividendGrowth.type === 'market' ? 'bg-theme-secondary text-theme-primary shadow' : 'text-theme-muted hover:text-theme-primary'} `}
                                    >
                                        Blue Chip (5%)
                                    </button>
                                    <button
                                        onClick={() => setDividendGrowth({ ...dividendGrowth, type: 'custom' })}
                                        className={`flex - 1 py - 2 text - sm rounded - md transition - all ${dividendGrowth.type === 'custom' ? 'bg-theme-secondary text-theme-primary shadow' : 'text-theme-muted hover:text-theme-primary'} `}
                                    >
                                        Custom
                                    </button>
                                </div>
                                {dividendGrowth.type === 'custom' && (
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={String(dividendGrowth.value)}
                                            onChange={(e) => setDividendGrowth({ type: 'custom', value: Number(e.target.value) })}
                                            className="w-full bg-theme-primary border border-theme-tertiary rounded-md py-2 px-3 text-theme-primary focus:ring-2 focus:ring-blue-500 outline-none no-spinner"
                                        />
                                        <span className="absolute right-3 top-2 text-theme-muted">%</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Results Section (Locked/Blurred) */}
                        <div className="relative mt-8 pt-8 border-t border-theme-tertiary">
                            <div className={`transition - all duration - 500 ${showResult ? 'opacity-100 blur-0' : 'opacity-40 blur-sm select-none'} `}>
                                <div className="text-center mb-6">
                                    <p className="text-3xl font-bold text-theme-primary">
                                        You'll reach <span className="text-blue-400">${goalAmount.toLocaleString()}</span>
                                    </p>
                                    <p className="text-xl text-theme-secondary mt-2">
                                        in approximately <span className="text-emerald-400 font-bold">{yearsToGoal} years</span> 🎯
                                    </p>
                                </div>

                                <div className="h-64 sm:h-80 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={chartData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                                            <XAxis
                                                dataKey="year"
                                                stroke="#9ca3af"
                                                tick={{ fontSize: 12 }}
                                                axisLine={false}
                                                tickLine={false}
                                            />
                                            <YAxis
                                                stroke="#9ca3af"
                                                tickFormatter={(val) => `$${val / 1000} k`}
                                                tick={{ fontSize: 12 }}
                                                axisLine={false}
                                                tickLine={false}
                                            />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', borderRadius: '0.5rem' }}
                                                itemStyle={{ color: '#e5e7eb' }}
                                                formatter={(value: number, name: string) => {
                                                    if (name === 'return') return [`$${value.toLocaleString()} `, 'Growth (Interest)'];
                                                    if (name === 'contributions') return [`$${value.toLocaleString()} `, 'Your Contributions'];
                                                    return [`$${value.toLocaleString()} `, name];
                                                }}
                                                labelStyle={{ color: '#9ca3af' }}
                                            />
                                            {/* Stacked Bars */}
                                            <Bar
                                                dataKey="contributions"
                                                stackId="a"
                                                fill="#34d399"
                                                radius={[0, 0, 4, 4]}
                                            />
                                            <Bar
                                                dataKey="return"
                                                stackId="a"
                                                fill="#3b82f6"
                                                radius={[4, 4, 0, 0]}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Lock Overlay */}
                            {!showResult && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                                    <div className="bg-theme-secondary/90 backdrop-blur-md p-8 rounded-2xl border border-theme-tertiary shadow-2xl text-center max-w-sm mx-auto">
                                        <div className="bg-blue-500/20 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                            <Lock className="w-8 h-8 text-blue-400" />
                                        </div>
                                        <h3 className="text-xl font-bold text-theme-primary mb-2">Unlock Your Projection</h3>
                                        <p className="text-theme-muted text-sm mb-6">
                                            See exactly when you can retire based on your current inputs.
                                        </p>
                                        <button
                                            onClick={handleReveal}
                                            className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 group"
                                        >
                                            See Results
                                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}
